import { useEffect, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
    getAppDeepLinkTarget,
    type AppDeepLinkTarget,
} from '@/utils/appDeepLinkTarget';
import { isMobileUserAgent, launchApp } from '@/utils/launchApp';

/**
 * Global "Open in App" banner.
 *
 * Renders a fixed top bar on mobile-web visitors only (UA-gated). Tapping the
 * CTA tries to open the native Nova One app via custom-scheme deep link, with
 * App Store / Play Store fallback after ~2s — see `src/utils/launchApp.ts`.
 *
 * Visibility model — read in tandem with the spec's decision 3:
 *   - Mobile UA: always visible, all routes (login, dashboard, anywhere).
 *   - Desktop UA: never rendered (returns null).
 *   - Not dismissable. We deliberately don't read sessionStorage / localStorage
 *     for a "dismissed" flag.
 *
 * Where it lives: injected once at the React tree root (see `app/root.tsx`),
 * outside the RouterProvider, so:
 *   - It survives across SPA navigations without rerendering.
 *   - It works on routes that render before AuthGuard (e.g. /login).
 *   - We don't depend on react-router hooks, which would force the banner
 *     inside the router subtree.
 *
 * The deep-link target is resolved at click time (never at mount time) so the
 * banner doesn't need to subscribe to navigation events — it always picks up
 * the latest state when the user actually taps the button. Two sources, in
 * priority order:
 *   1. `getAppDeepLinkTarget()` — pages whose entity isn't in the URL register
 *      themselves (MCP upload landing page, Material Request flyout).
 *   2. `window.location` pattern matching — task / issue / asset / purchase
 *      detail deep links (see `resolveLaunchTarget`).
 */

const TASK_DETAIL_PATH_RE = /^\/tasks\/(\d+)/;
// `/intents/:id` is a legacy deep link that redirects to `/intents?intentId=`
// (see app/routes/main/intents/$id.tsx) — match both shapes, since the banner
// can be tapped before or after that redirect lands.
const INTENT_DETAIL_PATH_RE = /^\/intents\/(\d+)/;
const INTENT_LIST_PATH_RE = /^\/intents(\/|$)/;
const PURCHASE_DETAIL_PATH_RE = /^\/procurement\/(\d+)/;
const PURCHASE_LIST_PATH_RE = /^\/procurement(\/|$)/;
const ASSET_LIST_PATH_RE = /^\/asset-mgr\/assets(\/|$)/;
const NUMERIC_ID_RE = /^\d+$/;

/** Read a numeric-id query param, ignoring anything that isn't a bare id. */
function numericParam(search: string, key: string): string | null {
    const value = new URLSearchParams(search).get(key);
    return value && NUMERIC_ID_RE.test(value) ? value : null;
}

/**
 * Resolve what the app should open, reading `window.location` fresh at each
 * click. Registry wins over URL matching — see the module docstring.
 */
function resolveLaunchTarget(): AppDeepLinkTarget | null {
    const registered = getAppDeepLinkTarget();
    if (registered) return registered;

    const { pathname, search } = window.location;

    const taskMatch = pathname.match(TASK_DETAIL_PATH_RE);
    if (taskMatch) return { kind: 'task', id: taskMatch[1] };

    const intentMatch = pathname.match(INTENT_DETAIL_PATH_RE);
    if (intentMatch) return { kind: 'issue', id: intentMatch[1] };

    const purchaseMatch = pathname.match(PURCHASE_DETAIL_PATH_RE);
    if (purchaseMatch) return { kind: 'purchase', id: purchaseMatch[1] };

    // Query-param deep links (flyout-on-list-page pattern). Each is scoped to
    // its own route on purpose: a stray `intentId` / `assetId` param hanging
    // around on some unrelated page shouldn't hijack the button.
    if (INTENT_LIST_PATH_RE.test(pathname)) {
        const intentId = numericParam(search, 'intentId');
        if (intentId) return { kind: 'issue', id: intentId };
    }
    if (ASSET_LIST_PATH_RE.test(pathname)) {
        const assetId = numericParam(search, 'assetId');
        if (assetId) return { kind: 'asset', id: assetId };
    }
    // `/procurement?purchaseId=` is the shape notifications actually link to
    // (see entity-links.ts `entityHref`), so it matters as much as the
    // `/procurement/:id` page above.
    if (PURCHASE_LIST_PATH_RE.test(pathname)) {
        const purchaseId = numericParam(search, 'purchaseId');
        if (purchaseId) return { kind: 'purchase', id: purchaseId };
    }

    // Note: `/material-requests?id=` is deliberately absent — that URL carries
    // an expense id, but the app opens the *owning task's* Supplies tab, and
    // the task id isn't in the URL. That page registers its own target
    // (see app/routes/main/material-requests.tsx).
    return null;
}

/** 不出「Open in App」横幅的公开外部页（访客不是 Nova 用户）。 */
export function isExternalPublicPath(pathname: string): boolean {
    return /^\/job\//.test(pathname);
}

export function OpenAppBanner(): JSX.Element | null {
    const { t } = useTranslation('chrome');
    // We use a state flag rather than calling `isMobileUserAgent()` inline
    // because navigator UA isn't available during SSR. There's no SSR in
    // main-web-new today, but this keeps the door open and avoids a
    // hydration-mismatch foot-gun if SSR/prerender is added later.
    const [show, setShow] = useState(false);

    useEffect(() => {
        // 外部承包商工单页（/job/:token）的访客不是 Nova 用户、没有 App 账号 ——
        // 「在 App 中打开」对他们是误导，还占掉手机首屏 56px。只看首次挂载时的路径：
        // 横幅不随 SPA 导航重渲染，而外部访客是直接打开链接进来的。
        if (isExternalPublicPath(window.location.pathname)) return;
        setShow(isMobileUserAgent());
    }, []);

    if (!show) return null;

    const onOpenApp = () => {
        launchApp(resolveLaunchTarget());
    };

    return (
        <>
            {/* Spacer pushes page content below the fixed banner. Lives at the
                same level as the banner so it's only present when the banner is. */}
            <div aria-hidden="true" style={{ height: 56 }} />
            <div
                role="region"
                aria-label={t('openAppBanner.regionLabel')}
                data-testid="open-app-banner"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 56,
                    background: 'hsl(var(--card))',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 12px',
                    zIndex: 1000,
                    fontSize: 14,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        minWidth: 0,
                    }}
                >
                    <img
                        src="/nova-icon.png"
                        alt=""
                        width={32}
                        height={32}
                        style={{ borderRadius: 6, flexShrink: 0 }}
                    />
                    <span
                        style={{
                            color: 'hsl(var(--card-foreground))',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {t('openAppBanner.description')}
                    </span>
                </div>
                <button
                    type="button"
                    data-testid="open-app-banner-cta"
                    onClick={onOpenApp}
                    style={{
                        background: '#3A4B76', // 品牌深蓝（对外分享页，不跟随主题）
                        color: '#fff',
                        border: 'none',
                        borderRadius: 999,
                        padding: '6px 16px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        flexShrink: 0,
                    }}
                >
                    {t('openAppBanner.cta')}
                </button>
            </div>
        </>
    );
}
